// client/src/components/RewardPopup.js
// Reward popup component with confetti and video/GIF animation

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Confetti from 'react-confetti';
import { HeadlineMedium, LabelMedium } from './Typography';

const RewardPopup = ({ 
  isVisible, 
  onClose, 
  levelNumber, 
  rewardType = 'level_complete',
  customMessage = null,
  videoSrc = null,
  gifSrc = null,
  audioSrc = null,
  duration = 10000 // 10 seconds default
}) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  /* isMuted tracks whether the user has silenced the reward audio */
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);
  // Ref for the close button so focus can be moved to it when the dialog opens
  const closeButtonRef = useRef(null);
  // Ref for the dialog container to implement focus trap
  const dialogRef = useRef(null);

  // Update window dimensions on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Trigger confetti and audio when popup becomes visible
  useEffect(() => {
    if (isVisible) {
      setShowConfetti(true);
      
      // Play audio if available
      if (audioSrc && audioRef.current) {
        audioRef.current.currentTime = 0; // Reset to beginning
        audioRef.current.volume = 0.7; // Set volume to 70%
        
        // Store reference for cleanup
        const audioElement = audioRef.current;
        
        audioElement.play().catch(error => {
          // Try to play again after a short delay (user interaction might be needed)
          setTimeout(() => {
            if (audioElement) {
              audioElement.play().catch(() => {
                // Silently fail on retry
              });
            }
          }, 100);
        });
        
        // Stop confetti after the full duration (10 seconds)
        const confettiTimer = setTimeout(() => {
          setShowConfetti(false);
        }, duration);

        // Auto-close popup after duration
        const closeTimer = setTimeout(() => {
          onClose();
        }, duration);

        return () => {
          clearTimeout(confettiTimer);
          clearTimeout(closeTimer);
          // Pause audio when popup closes
          if (audioElement) {
            audioElement.pause();
          }
        };
      }
    }
  }, [isVisible, duration, onClose, audioSrc]);

  // Move focus to the close button when the dialog opens
  useEffect(() => {
    if (isVisible && closeButtonRef.current) {
      setTimeout(() => {
        if (closeButtonRef.current) closeButtonRef.current.focus();
      }, 0);
    }
  }, [isVisible]);

  // Focus trap: keep Tab / Shift+Tab inside the dialog while it is visible
  const handleDialogKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onClose]);

  // User interaction listener to help audio autoplay after a user gesture
  useEffect(() => {
    const handleUserInteraction = () => {
      // Try to play audio on any user interaction (helps with autoplay policies)
      if (isVisible && audioSrc && audioRef.current) {
        audioRef.current.play().catch(() => {
          // Silently fail - user interaction will trigger it
        });
      }
    };

    if (isVisible) {
      document.addEventListener('click', handleUserInteraction);
      document.addEventListener('touchstart', handleUserInteraction);
      return () => {
        document.removeEventListener('click', handleUserInteraction);
        document.removeEventListener('touchstart', handleUserInteraction);
      };
    }
  }, [isVisible, audioSrc]);

  /* Toggle audio mute — satisfies WCAG 1.4.2 Audio Control */
  const handleToggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    if (audioRef.current) audioRef.current.muted = next;
  };

  if (!isVisible) return null;

  // Get reward content based on type
  const getRewardContent = () => {
    switch (rewardType) {
      case 'level_complete':
        return {
          title: `Level ${levelNumber} Complete!`,
          message: customMessage || `Congratulations! You've successfully completed Level ${levelNumber}.`,
          icon: '🎉'
        };
      case 'course_complete':
        return {
          title: 'Course Complete!',
          message: customMessage || 'Amazing work! You\'ve finished the entire course.',
          icon: '🏆'
        };
      case 'achievement':
        return {
          title: 'Achievement Unlocked!',
          message: customMessage || 'You\'ve earned a new achievement!',
          icon: '⭐'
        };
      default:
        return {
          title: 'Congratulations!',
          message: customMessage || 'Great job!',
          icon: '🎊'
        };
    }
  };

  const rewardContent = getRewardContent();

  return (
    <>
      {/* Audio Element */}
      {audioSrc && (
        <audio
          ref={audioRef}
          src={audioSrc}
          preload="auto"
          loop
        />
      )}

      {/* Confetti Animation — skipped when OS prefers reduced motion (WCAG 2.3.3) */}
      {showConfetti && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && (
        <Confetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          recycle={true}
          numberOfPieces={500}
          gravity={0.2}
          initialVelocityY={15}
          initialVelocityX={5}
          colors={['#1E40AF', '#3B82F6', '#60A5FA', '#93C5FD', '#DBEAFE', '#1F2937', '#374151', '#6B7280']}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 1000,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* Popup Overlay — z-[1001] sits above confetti (z-index 1000) so the dialog
          is never visually obscured by confetti pieces (WCAG 2.4.3 Focus Order) */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1001] p-4">
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="reward-popup-title"
          className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
          onKeyDown={handleDialogKeyDown}
        >
          {/* Mute/unmute button — only shown when audio is present; satisfies WCAG 1.4.2 */}
          {audioSrc && (
            <button
              onClick={handleToggleMute}
              className="absolute top-4 right-14 z-10 p-2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              aria-label={isMuted ? 'Unmute reward audio' : 'Mute reward audio'}
              aria-pressed={isMuted}
            >
              {isMuted ? (
                /* Speaker with X — muted state (Heroicons volume-off) */
                <svg aria-hidden="true" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9l4 4m0-4l-4 4" />
                </svg>
              ) : (
                /* Speaker with sound waves — unmuted state (Heroicons volume-up) */
                <svg aria-hidden="true" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728" />
                </svg>
              )}
            </button>
          )}

          {/* Close Button — receives focus first when dialog opens */}
          <button
            ref={closeButtonRef}
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            aria-label="Close reward dialog"
          >
            <svg aria-hidden="true" className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Content */}
          <div className="p-8 text-center">
            {/* Reward Icon/Animation — decorative, title and message convey the meaning */}
            <div className="mb-6" aria-hidden="true">
              {videoSrc ? (
                <div className="relative inline-block">
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full max-w-md mx-auto object-contain shadow-lg"
                  >
                    <source src={videoSrc} type="video/mp4" />
                    <source src={videoSrc.replace('.mp4', '.webm')} type="video/webm" />
                    <img
                      src={gifSrc}
                      alt=""
                      className="w-full max-w-md mx-auto object-contain shadow-lg"
                    />
                  </video>
                </div>
              ) : gifSrc ? (
                <img
                  src={gifSrc}
                  alt=""
                  className="w-full max-w-md mx-auto object-contain shadow-lg"
                />
              ) : (
                <div className="w-32 h-32 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-6xl shadow-lg">
                  {rewardContent.icon}
                </div>
              )}
            </div>

            {/* Title — referenced by aria-labelledby on the dialog */}
            <HeadlineMedium id="reward-popup-title" color="gray-900" className="mb-4">
              {rewardContent.title}
            </HeadlineMedium>

            {/* Message */}
            <LabelMedium color="gray-600" className="mb-6">
              {rewardContent.message}
            </LabelMedium>

            {/* Action Buttons */}
            <div className="flex justify-center">
              <button
                onClick={onClose}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Continue Learning
              </button>
            </div>
          </div>

          {/* Decorative gradient bar */}
          <div aria-hidden="true" className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500"></div>
        </div>
      </div>
    </>
  );
};

export default RewardPopup;
